import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import Gravatar from 'react-gravatar'
import { useSelector, useDispatch } from 'react-redux'
import { editTenant } from 'api/tenant'
import { cancelEmailChange, fetchEmailChangeStatus } from 'api/user'
import { showModal, closeModal } from 'actions/app'
import { TEST_IDS } from 'test-ids'
import ModalHeader from 'components/modal/modal-header'
import ModalActions from 'components/modal/modal-actions'
import UserPreference from './user-preference'
import ChangeEmailDialog from './change-email'

const typographyStyle = {
  width: '50ch',
  paddingLeft: '10px',
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: '0em',
  textAlign: 'left',
  fontFamily: 'Roboto',
}

export default function UserDetails({ onScroll }) {
  const dispatch = useDispatch()
  const user = useSelector((state) => state.account['singleton'])
  const [editing, setEditing] = useState(false)
  const [formFields, setFormFields] = useState({
    fullName: user.fullName,
    email: user.email,
    phoneNumber: user.phoneNumber,
    streetAddress: user.streetAddress,
    postalCode: user.postalCode,
  })

  const { t } = useTranslation()

  const [changeEmailOpen, setChangeEmailOpen] = useState(false)
  const [pendingEmailChange, setPendingEmailChange] = useState(null)
  const [emailChangeNotice, setEmailChangeNotice] = useState('')

  const refreshEmailChangeStatus = useCallback(() => {
    dispatch(fetchEmailChangeStatus())
      .then((res) => {
        setPendingEmailChange(res?.data?.status === 'pending' ? res.data : null)
      })
      .catch(() => {})
  }, [dispatch])

  useEffect(() => {
    refreshEmailChangeStatus()
  }, [refreshEmailChangeStatus])

  const handleCancelPendingEmailChange = () => {
    dispatch(cancelEmailChange())
      .then(() => {
        setPendingEmailChange(null)
        setEmailChangeNotice(t('email_change_cancelled'))
      })
      .catch(() => {})
  }

  const fields = [
    { label: t('full_name'), fieldName: 'fullName' },
    { label: t('email'), fieldName: 'email' },
    { label: t('phone_number'), fieldName: 'phoneNumber' },
    { label: t('street_address'), fieldName: 'streetAddress' },
    { label: t('postal_code'), fieldName: 'postalCode' },
  ]

  const handleInputChange = (e) => {
    if (e) {
      const { name, value } = e.target
      setFormFields((formFields) => ({
        ...formFields,
        [name]: value,
      }))
    }
  }

  const saveInput = () => {
    dispatch(
      editTenant({
        fullName: formFields.fullName,
        phoneNumber: formFields.phoneNumber,
        streetAddress: formFields.streetAddress,
        postalCode: formFields.postalCode,
      }),
    ).then(() => {
      dispatch(showModal(<UserDetails />, { maxWidth: 'md', fullWidth: true }))
    })
  }

  const openModifyPrefs = () => {
    dispatch(showModal(<UserPreference />))
  }

  const closePopup = useCallback(() => {
    dispatch(closeModal())
  }, [dispatch])

  return (
    // </>
    <Box
      onScroll={onScroll}
      sx={{
        px: 3,
        py: 4,
        display: 'flex',
        maxHeight: '70vh',
        margin: 'auto',
        overflowY: 'auto',
        padding: '2rem',
        maxWidth: '60vw',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <ModalHeader>{t('account')}</ModalHeader>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: 'column',
        }}
      >
        <Gravatar
          email={formFields.email}
          size={100}
          default="identicon"
          protocol="https://"
        />
        <br />

        {editing ? (
          <div>
            {fields.map(({ label, fieldName }) => (
              <FormControlLabel
                key={fieldName}
                label={label}
                labelPlacement="start"
                sx={{ width: '100%' }}
                control={
                  <TextField
                    fullWidth
                    disabled={fieldName === 'email'}
                    type={
                      fieldName === 'email'
                        ? 'email'
                        : fieldName === 'phoneNumber'
                          ? 'tel'
                          : 'text'
                    }
                    margin="normal"
                    name={fieldName}
                    variant="outlined"
                    defaultValue={formFields[fieldName]}
                    onChange={handleInputChange}
                    sx={{
                      width: '50ch',
                      paddingLeft: '10px',
                    }}
                  />
                }
              />
            ))}
          </div>
        ) : (
          <div>
            {fields.map(({ label, fieldName }) => (
              <FormControlLabel
                key={fieldName}
                label={label}
                labelPlacement="start"
                sx={{ width: '100%', padding: '10px' }}
                control={
                  <Typography sx={typographyStyle}>
                    {formFields[fieldName]}
                  </Typography>
                }
              />
            ))}
          </div>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            marginTop: '1rem',
          }}
        >
          {editing ? (
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={() => {
                saveInput()
                setEditing(false)
              }}
            >
              {t('save_profile')}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              size="large"
            >
              {t('edit_profile')}
            </Button>
          )}
          {!pendingEmailChange && (
            <Button
              variant="contained"
              size="large"
              onClick={() => setChangeEmailOpen(true)}
              data-testid={TEST_IDS.changeEmailButton}
            >
              {t('change_email')}
            </Button>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            maxWidth: '50ch',
            textAlign: 'center',
            marginTop: '1rem',
          }}
        >
          {emailChangeNotice && (
            <Alert
              severity="success"
              sx={{ mb: 1 }}
              onClose={() => setEmailChangeNotice('')}
            >
              {emailChangeNotice}
            </Alert>
          )}
          {pendingEmailChange && (
            <Alert
              severity="info"
              data-testid={TEST_IDS.changeEmailPendingBanner}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleCancelPendingEmailChange}
                  data-testid={TEST_IDS.changeEmailCancelPendingButton}
                >
                  {t('email_change_cancel')}
                </Button>
              }
            >
              {t('email_change_pending', {
                email: pendingEmailChange.newEmail,
              })}
              <br />
              {t('email_change_pending_hint')}
            </Alert>
          )}
        </Box>
        <br />
      </div>
      <ChangeEmailDialog
        open={changeEmailOpen}
        onClose={() => setChangeEmailOpen(false)}
        onChangeInitiated={() => {
          setEmailChangeNotice(t('email_change_sent'))
          refreshEmailChangeStatus()
        }}
      />
      <Grid size={12} align={'center'}>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => openModifyPrefs(true)}
          size="large"
        >
          {t('modify_notification_preferences')}
        </Button>
      </Grid>
      <ModalActions>
        <Button
          variant="contained"
          onClick={closePopup}
          data-testid={TEST_IDS.userDetailsCloseButton}
        >
          {t('close')}
        </Button>
      </ModalActions>
    </Box>
  )
}
